import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { FOUNDER } from '@/config/founder';
import { Linkedin, Instagram } from 'lucide-react';

/**
 * MarketingFooter — shared footer for every public marketing page
 * (Landing, /about, /pricing, /features, /customers).
 *
 * Phase 92 (site audit): adds the founder block (config-driven via
 * /config/founder.js) and links to the new dedicated pages so crawlers
 * and visitors can reach /about, /pricing, /features, /customers from
 * anywhere.
 */
export function MarketingFooter() {
    return (
        <footer className="bg-[#0a1628] py-10 text-white" data-testid="marketing-footer">
            <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 text-sm sm:px-6 md:flex-row">
                <div className="flex items-center gap-3">
                    <Logo light testId="sp-logo-footer" />
                </div>
                <div className="text-white/60">SplashPad by SSU · Powered by Superhero Swim University</div>
                <div className="flex flex-wrap items-center justify-center gap-4 text-white/70">
                    <Link to="/about" className="hover:text-white" data-testid="footer-about-link">About</Link>
                    <Link to="/features" className="hover:text-white" data-testid="footer-features-link">Features</Link>
                    <Link to="/pricing" className="hover:text-white" data-testid="footer-pricing-link">Pricing</Link>
                    <Link to="/customers" className="hover:text-white" data-testid="footer-customers-link">Customers</Link>
                    <Link to="/blog" className="hover:text-white" data-testid="legal-footer-blog-link">Guides</Link>
                    <Link to="/security" className="hover:text-white" data-testid="landing-security-link">Security</Link>
                    <Link to="/login" className="hover:text-white">Log in</Link>
                    <Link to="/privacy" className="hover:text-white" data-testid="landing-privacy-link">Privacy</Link>
                    <Link to="/terms" className="hover:text-white" data-testid="landing-terms-link">Terms</Link>
                    <Link to="/accessibility" className="hover:text-white" data-testid="landing-accessibility-link">Accessibility</Link>
                    <Link to="/privacy/delete-my-data" className="hover:text-white" data-testid="landing-privacy-choices-link">Your Privacy Choices</Link>
                </div>
            </div>

            {/* Founder block — hidden until /config/founder.js is filled in.
                The audit calls the anonymous founder the #1 trust gap; once
                the owner supplies name + photo + LinkedIn this renders on
                every marketing page. */}
            {FOUNDER.enabled && FOUNDER.name && (
                <div className="mx-auto mt-6 flex max-w-7xl items-center justify-center gap-3 px-4 sm:px-6" data-testid="footer-founder-block">
                    {FOUNDER.photoUrl && (
                        <img
                            src={FOUNDER.photoUrl}
                            alt={`${FOUNDER.name}, founder of SplashPad`}
                            className="h-10 w-10 rounded-full object-cover ring-2 ring-white/20"
                            loading="lazy"
                        />
                    )}
                    <div className="text-xs text-white/70">
                        Built by <span className="font-semibold text-white">{FOUNDER.name}</span> — {FOUNDER.bio}
                        {FOUNDER.linkedinUrl && (
                            <a
                                href={FOUNDER.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-2 inline-flex items-center gap-1 text-[#00d4ff] hover:underline"
                                data-testid="footer-founder-linkedin"
                            >
                                <Linkedin className="h-3 w-3" /> LinkedIn
                            </a>
                        )}
                        {FOUNDER.instagramUrl && (
                            <a
                                href={FOUNDER.instagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-2 inline-flex items-center gap-1 text-[#00d4ff] hover:underline"
                                data-testid="footer-founder-instagram"
                            >
                                <Instagram className="h-3 w-3" /> @superheroswim
                            </a>
                        )}
                    </div>
                </div>
            )}

            <div className="mx-auto mt-6 max-w-7xl border-t border-white/10 px-4 pt-4 text-center text-xs text-white/50 sm:px-6">
                © {new Date().getFullYear()} <span className="font-semibold text-white/70">SSA MEDIA LLC</span> · <a href="mailto:support@splashpad.pro" className="hover:text-white">support@splashpad.pro</a>
            </div>
        </footer>
    );
}

/**
 * MarketingNav — lightweight sticky header for the dedicated marketing
 * pages (/about, /pricing, /features, /customers). The homepage keeps its
 * own richer nav with in-page anchors.
 */
export function MarketingNav() {
    return (
        <header className="sticky top-0 z-30 sd-glass border-b" data-testid="marketing-nav">
            <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
                <Link to="/" aria-label="SplashPad home"><Logo /></Link>
                <nav className="hidden items-center gap-6 text-sm md:flex">
                    <Link to="/features" className="text-[#1a3a5c] hover:text-[#0a1628]">Features</Link>
                    <Link to="/pricing" className="text-[#1a3a5c] hover:text-[#0a1628]">Pricing</Link>
                    <Link to="/customers" className="text-[#1a3a5c] hover:text-[#0a1628]">Customers</Link>
                    <Link to="/about" className="text-[#1a3a5c] hover:text-[#0a1628]">About</Link>
                </nav>
                <div className="flex items-center gap-2">
                    <Link to="/login" className="rounded-lg px-3 py-2 text-sm text-[#1a3a5c] hover:text-[#0a1628]">Log in</Link>
                    <Link to="/signup?role=instructor" className="sd-cta sd-focus inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold" data-testid="marketing-nav-trial">Start Free Trial</Link>
                </div>
            </div>
        </header>
    );
}
